import webglHome from 'config/webgl-home';

import { particles } from 'app/contracts/Particles';
import { ParticleType, ParticleFieldType, HomeParticleStateType } from 'app/enum/Particles';

import { HomeParticleManager } from 'app/views/home/webgl/particles/fields/HomeParticleManager';
import HomeScreens from 'app/views/home/webgl/particles/scenes/HomeScreens';
import HomeFieldConfig from 'app/views/home/webgl/particles/config/HomeFieldConfig';
import HomeFieldScreensPixelDataConfig from 'app/views/home/webgl/particles/config/HomeFieldScreensPixelDataConfig';
import LogoPixelCache from 'app/data/HomePixelCache/LogoPixelCache';
import ParticleGridTargets from 'app/views/home/webgl/particles/targets/ParticleGridTargets';
import ParticleBehaviourLookup from 'particles/ParticleBehaviourLookup';

//Lookups for paths to use:
import { CircleLookup } from 'particles/lookups/CircleLookup';
import { SineLookup } from 'particles/lookups/SineLookup';
import { IconLookup } from 'particles/lookups/IconLookup';
import { IconLookup2 } from 'particles/lookups/IconLookup2';
import { TriLookup } from 'particles/lookups/TriLookup';
import { RectLookup } from 'particles/lookups/RectLookup';

import { GridLayout } from 'particles/layouts/GridLayout';
import { RandomLayout } from 'particles/layouts/RandomLayout';
import { PerpectiveLayout } from 'particles/layouts/PerpectiveLayout';
import { IsometricGridLayout } from 'particles/layouts/IsometricGridLayout';
import { ImageGridLayout } from 'particles/layouts/ImageGridLayout';

//Targets for the particles to track:
import { MouseTarget } from 'particles/targets/MouseTarget';
import { ScreenTarget } from 'particles/targets/ScreenTarget';
import { SceneTarget } from 'particles/targets/SceneTarget';

import type { DepartmentType } from 'app/data/Enums';
import type { Point } from 'app/contracts/Geometry';
import type {
	HomeItemGeometry,
	HomeStageSlots,
	HomeWebGL,
	HomeWebGLDeps,
	HomeWebGLFrame,
	HomeWebGLSizes,
	HomeWebGLType
} from 'app/views/home/webgl/webgl.types';

/**
 * ParticlesWebGL — the 'particles' implementation of the home WebGL layer.
 * A facade over HomeParticleManager (which keeps working unchanged
 * internally): it owns the swap side of the home scene — the particle
 * manager, the swap-only sceneProps members (lookups / layouts / targets /
 * colours, augmented onto the timeline's shared sceneProps object), the
 * per-frame sceneProps.mouse writes, the icon/systems container fades and
 * the preload handshake (reportPixelDataReady / reportSceneCreated).
 *
 * Implements the full HomeWebGL contract (seam commits A + B): lifecycle
 * (create / tick / resize / freeze), the work transitions, the intro
 * handshake and the region/explore hooks. Each contract method is the
 * swap-side half of the pre-seam HomeTimeline method it moved out of —
 * the item/DOM halves stay in the timeline, which drives this class only
 * through the contract (it has no particle-manager access).
 *
 * TIMING: this must be constructed inside HomeTimeline's constructor and
 * create() must be called in the same task (HomeScene.createTimelines does
 * both synchronously). The constructor performs the (previously
 * constructor-inline) pixel-data load — which fires reportPixelDataReady —
 * and schedules the deferred icon pixel push, which assumes _manager exists
 * by the time the macrotask fires (exactly the pre-seam behaviour, where the
 * setTimeout in HomeTimeline's constructor dereferenced a
 * _particleManager that only exists once createScene has run).
 */
export class ParticlesWebGL implements HomeWebGL {
	public readonly type: HomeWebGLType = 'particles';

	private _deps: HomeWebGLDeps;

	//The timeline's shared-identity scene props bag (deps.getSceneProps()),
	//augmented with the swap-only members in create():
	private _sceneProps: particles.ParticlesSceneProps;
	private _slots: HomeStageSlots;
	private _manager: HomeParticleManager;

	//Read access to the timeline's big-item geometry (create() argument) —
	//HomeScreens' TV-frame / hit-block / glow wiring reads items through it:
	private _geometry: HomeItemGeometry;

	constructor(deps: HomeWebGLDeps) {
		this._deps = deps;

		//Deferred icon-grid pixel push by cache (ex HomeTimeline
		//constructor, verbatim incl. the no-guard _manager dereference —
		//see the class TIMING note):
		setTimeout(() => {
			this._manager.setIconPixelData({
				icon: LogoPixelCache.data
			});
		});

		//Load the Icon Grid Data by cache:

		this._loadPixelData();
	}

	//Create about 8000 particles. These are never used together, they activated / deactived for each view
	public create(
		slots: HomeStageSlots,
		spritesheet: PIXI.Spritesheet,
		sizes: HomeWebGLSizes,
		geometry: HomeItemGeometry
	): void {
		this._slots = slots;
		this._geometry = geometry;
		//The ONE Shared→Particles narrowing: the deps hand over the shared
		//context and _augmentSceneProps() (next line, same object) supplies
		//the swap-only members the ParticlesSceneProps type promises:
		this._sceneProps = this._deps.getSceneProps() as particles.ParticlesSceneProps;
		this._augmentSceneProps();

		//Guide container for the systems dot grid (ex createScene):
		let guidContainer = new PIXI.Container();
		this._slots.guides.addChild(guidContainer);

		ParticleGridTargets.setGuideContainer(guidContainer);

		//The texture dictionary rides on sceneProps (ex globals.textures) —
		//ParticlesStage and HomeParticle read it, and both are constructed
		//after this write (the manager below builds the fields):
		this._sceneProps.textures = spritesheet.textures;

		let particleConfig: particles.ParticleSetConfig = {
			particles: null,
			totalIconParticles: 947,
			fields: [
				{
					type: ParticleFieldType.HOME,
					id: 'home-particle-field',
					count: HomeFieldConfig.total(),
					position: { x: 0.33333333, y: 0 },
					size: { w: 0.33333, h: 1.0 },
					z: 10,
					particles: {
						types: ParticleType.HOME,
						textures: [spritesheet.textures['solid2.png']],
						tints: this._sceneProps.colours.arrays.homeOpening
					}
				},

				//Create the perpective grid used on spaces side
				//Also includes the layered BG texture elements
				{
					type: ParticleFieldType.SPACES_GRID,
					id: 'spaces-particle-field',
					count: 1,
					position: { x: 0.36333 + 0.41, y: 0.45 },
					size: { w: 0.04, h: 0.4 },
					z: 10,
					particles: {
						types: ParticleType.SPACES_GRID,
						textures: [spritesheet.textures['solid.png']],
						tints: this._sceneProps.colours.arrays.homeOpening
					}
				}

				//Create the regular - semi broken grid seen on the sreens section:
			] //End of fields
		};

		//The particle fields mount into the webglMain slot (between the two
		//big-item passes):
		//The manager threads the deps down to the fields cluster (HomeField
		//px->rel conversion via getVirtualWidth, DepartmentColours'
		//upward colourise calls — previously a TimelineFieldApi adapter):
		this._manager = new HomeParticleManager(this._sceneProps, this._slots.webglMain, particleConfig, this._deps);
		this._manager.onParticlesReady = () => {
			//Screens-TV overlay wiring first (ex HomeTimeline
			//._initScreensElements — it ran at the top of the old onReady
			//continuation, before the manager resize; nothing between it and
			//the shared half reads HomeScreens, so hoisting it above onReady()
			//is order-equivalent). ParticlesStage has long since built the
			//darken/overlay containers HomeScreens.init draws into — it is
			//constructed synchronously during field creation, while this
			//callback fires two setTimeouts later:
			this._initScreens();

			//Shared continuation (timeline: screens-TV sprite refs + the
			//container-reveal timeout), then the swap-side resize + intro
			//start — same effective order as the pre-seam handler:
			this._deps.onReady();
			this._manager.resize();

			setTimeout(() => {
				this._manager.changeState(HomeParticleStateType.OPENING);
				this._manager.homeField.startIntroScene();
			}, webglHome.revealDelaysMs.sceneStart);
		};

		this._deps.reportSceneCreated();
	}

	public tick(frame: HomeWebGLFrame): void {
		this._setMouse(frame);
		this._setMouseParallax();
		this._setMouseProgress(frame);

		this._manager.tick();

		//Icon / systems container fades over drag progress — the containers
		//are ParticlesStage sub-containers, reached through the field (the
		//manager's homeField ref is set a beat after field creation; until
		//then the home container is still hidden, so skipping the fades is
		//equivalent to the old null-global guards):
		const stage = this._manager.homeField ? this._manager.homeField.stage : null;

		if (stage) {
			stage.iconContainer.alpha = Math.max(0, 1 - frame.progress / webglHome.containerFades.iconFadeOutSpan);

			stage.systemsContainer.alpha = Math.max(
				0,
				(frame.progress - webglHome.containerFades.systemsFadeInStart) / webglHome.containerFades.systemsFadeInSpan
			);
		}

		//Screens-TV overlay redraw (mask / darken / overlay positions off the
		//shared item sprites). Pre-seam it ran after the timeline's cssSync
		//tick; neither reads the other's writes (HomeScreens touches only its
		//Pixi graphics + bounds cache, cssSync only DOM/CSS), so running it
		//here — after the manager tick, same as before — is order-equivalent:
		HomeScreens.tick();
	}

	public resize(sizes: HomeWebGLSizes, firstResize: boolean): void {
		if (!this._manager) {
			return;
		}

		this._manager.resize();

		if (firstResize && this._manager.homeField) {
			this._manager.homeField.resizeEvent();
		}

		//Invalidate the systems dot grid (ex HomeScene.applyContainerResize):
		ParticleGridTargets.resize(sizes.innerWidth, sizes.innerHeight);
	}

	public freeze(): void {
		ParticleBehaviourLookup.pause();
		this._manager.homeField.freeze();
	}

	public unfreeze(): void {
		ParticleBehaviourLookup.unpause();
		this._manager.homeField.unfreeze();
	}

	//Contract members completed in seam commit B. Bodies are the swap halves
	//of the pre-seam HomeTimeline methods, verbatim; members documented as
	//no-ops exist because the contract names the hook but today's code has no
	//swap-side work there — the timeline still calls them, so a future
	//implementation receives the full event stream.
	//-----------------------------------------------------------------------------------------------

	/**
	 * Show/hide the layer's own mounted content (the webglMain slot). Nothing
	 * routes here today — home-container ($homeContainer) visibility is
	 * handled by HomeScene / HomeTimeline / HomeController on the shared
	 * side and stays there.
	 */
	public show(): void {
		if (this._slots) {
			this._slots.webglMain.visible = true;
		}
	}

	public hide(): void {
		if (this._slots) {
			this._slots.webglMain.visible = false;
		}
	}

	/**
	 * The swap half of the timeline's reset paths. Today's resets
	 * (resetIntervalsAndAnimations / resetForDeeplink) are pure item/DOM
	 * loops on the shared side — no swap-side state to reset.
	 */
	public reset(): void {}

	/** Swap half of the back-from-work reset — today's is a pure item loop + DOM y-offset (shared side). */
	public resetFromWork(department: DepartmentType, offY: number): void {}

	/** Swap half of the deeplink reset — pure item/DOM today (shared side). */
	public deeplink(department: DepartmentType | null): void {}

	public setupForWork(department: DepartmentType): void {
		this._manager.homeField.setupForWork(department);
	}

	public showFromWork(department: DepartmentType): void {
		this._manager.showFromWork(department);

		if (this._manager.homeField) {
			this._manager.homeField.showFromWork(department);
		}
	}

	//Driven externally per animation frame during the work-intro transition
	//(WorkController -> HomeController -> HomeTimeline -> here):
	public updateWorkTransitionParticles(introXPx: number, introProgressRatio: number): void {
		if (this._manager && this._manager.homeField) {
			this._manager.homeField.updateWorkTransitionParticles(introXPx, introProgressRatio);
		}
	}

	/**
	 * The particle intro scene starts from create()'s onParticlesReady handler
	 * (startIntroScene, seam commit A); the timeline's intro-start itself is
	 * item fades only — no swap half.
	 */
	public introStart(): void {}

	public introHover(): void {
		this._manager.changeState(HomeParticleStateType.OPENING_HOVER);
		this._manager.homeField.introMouseOver();
	}

	public introOut(): void {
		this._manager.changeState(HomeParticleStateType.OPENING);
		this._manager.homeField.introMouseOut();
	}

	//The swap calls of the pre-seam introComplete, in their original relative
	//order (the shared item fades/refs ran interleaved between them — none of
	//them are read by these calls, so grouping is order-equivalent). The
	//screens-TV reveal + glow wiring (ex HomeTimeline.homeIntroShow's
	//HomeScreens.showAtStart and the introComplete glows assignment) are the
	//swap half of the same intro-complete event; showAtStart before the glows
	//write preserves the pre-seam order (they are independent — showAtStart
	//never reads glows):
	public introComplete(): void {
		this._manager.homeField.introComplete();
		this._manager.changeState(HomeParticleStateType.HOME);
		this._manager.homeField.startMainScene();

		HomeScreens.showAtStart();
		HomeScreens.glows = this._geometry.getItems('screens-bg-blob-glow');
	}

	//Region cut-overs — the timeline's region machine keeps its own state
	//guards (e.g. HomeLogoArea only fires in the HOME timeline state) and
	//forwards the swap half here:
	public onRegionEnter(id: string): void {
		if (id === 'HomeLogoArea') {
			this._manager.iconHover();
		} else if (id === 'Home') {
			this._manager.changeState(HomeParticleStateType.HOME, true);
		} else if (id === 'Spaces') {
			this._manager.changeState(HomeParticleStateType.HOME_SPACES);
		}
	}

	/** No swap half today — region exits only clear shared sceneProps flags (timeline side). */
	public onRegionExit(id: string): void {}

	public onExploreHover(center: Point): void {
		this._manager.changeState(HomeParticleStateType.HOME);
	}

	/** No swap half today — explore-out sets item timeline state only (shared side). */
	public onExploreOut(department: DepartmentType): void {}

	/** No swap-side intervals/animations exist today — the item loop stays in the timeline. */
	public resetIntervalsAndAnimations(): void {}

	//Private
	//-----------------------------------------------------------------------------------------------

	/**
	 * Wire HomeScreens to the shared TV-frame / systems-block item sprites (ex
	 * HomeTimeline._initScreensElements' HomeScreens.init call, via the
	 * HomeItemGeometry contract instead of _getItemsById). Runs from the
	 * onParticlesReady handler — ParticlesStage built the darken/overlay
	 * containers HomeScreens draws into long before (synchronously during
	 * field creation), and the timeline items were all created around
	 * create():
	 */
	private _initScreens(): void {
		HomeScreens.init(
			{
				topLeft: this._geometry.getItems('screensTVTopLeft')[0],
				topRight: this._geometry.getItems('screensTVTopRight')[0],
				bottomLeft: this._geometry.getItems('screensTVBottomLeft')[0],
				bottomRight: this._geometry.getItems('screensTVBottomRight')[0]
			},
			this._geometry.getItems('systems-block').concat(this._geometry.getItems('systems-block-move'))
		);
	}

	private _loadPixelData() {
		let activeItems = HomeFieldScreensPixelDataConfig.getList();

		activeItems.forEach((pixelGridItem) => {
			HomeFieldScreensPixelDataConfig.loadPixels(pixelGridItem);
		});

		this._deps.reportPixelDataReady();
	}

	/**
	 * Augment the timeline's shared sceneProps object with the swap-only
	 * members (ex HomeTimeline._createSceneProps — bodies verbatim).
	 * Object identity never changes; every existing reader keeps working.
	 */
	private _augmentSceneProps(): void {
		let lookups: particles.ParticleLookups = {
			circle: new CircleLookup(),
			sine: new SineLookup(),
			icon: new IconLookup(),
			icon2: new IconLookup2(),
			tri: new TriLookup(),
			rect: new RectLookup()
		};

		let targets: particles.ParticleTargets = {
			mouse: new MouseTarget(),
			spacesMouse: new MouseTarget('spaces'),
			homeMouse: new MouseTarget('home'),
			screensMouse: new MouseTarget('screens'),
			systemsMouse: new MouseTarget('systems'),
			sceneCenter: new SceneTarget({ x: 0.5, y: 0.45 }),
			screenCenter: new ScreenTarget({ x: 0, y: 0.5 })
		};

		let layouts: particles.ParticleLayouts = {
			grid: new GridLayout(),
			random: new RandomLayout(),
			perspective: new PerpectiveLayout(),
			isometric: new IsometricGridLayout(),
			image_pixel_data: new ImageGridLayout(),
			image_pixel_data_2: new ImageGridLayout(),
			image_pixel_data_head: new ImageGridLayout()
		};

		let colours: particles.ParticleColours = {
			arrays: {
				homeOpening: [0x34dccd, 0x27746c, 0xcecece, 0xffffff, 0xebbcf0, 0xf24a8d, 0xcecece, 0xffffff],
				homeSpacesSide: [0x63b4b5, 0x1e5b6f, 0xcecece, 0xffffff],
				homeScreensSide: [0xebbcf0, 0xf24a8d, 0xcecece, 0xffffff]
			}
		};

		this._sceneProps.lookups = lookups;
		this._sceneProps.layouts = layouts;
		this._sceneProps.targets = targets;
		this._sceneProps.colours = colours;

		//Set scene props to the targets (So they can do calculations)
		this._sceneProps.targets.mouse.sceneProps = this._sceneProps;
		this._sceneProps.targets.sceneCenter.sceneProps = this._sceneProps;
		this._sceneProps.targets.screenCenter.sceneProps = this._sceneProps;
		this._sceneProps.targets.homeMouse.sceneProps = this._sceneProps;
		this._sceneProps.targets.spacesMouse.sceneProps = this._sceneProps;
		this._sceneProps.targets.screensMouse.sceneProps = this._sceneProps;
		this._sceneProps.targets.systemsMouse.sceneProps = this._sceneProps;
	}

	//Per-frame sceneProps.mouse writes (ex HomeTimeline._setMouse* —
	//bodies verbatim, sourced from the frame snapshot; all readers are
	//swap-side and read during _manager.tick(), after these writes):

	private _setMouse(frame: HomeWebGLFrame): void {
		this._sceneProps.mouse.cords = frame.mouseCords;
		this._sceneProps.mouse.isDown = frame.mouseIsDown;
		this._sceneProps.mouse.scene = {
			x: (frame.edgeX + this._sceneProps.mouse.cords.x) / this._sceneProps.sizes.container.w,
			y: this._sceneProps.mouse.cords.y / this._sceneProps.sizes.container.h
		};
	}

	private _setMouseParallax(): void {
		this._sceneProps.mouse.parallax.x = this._sceneProps.mouse.cords.x / this._sceneProps.sizes.window.w - 0.5;
		this._sceneProps.mouse.parallax.y = this._sceneProps.mouse.cords.y / this._sceneProps.sizes.window.h - 0.5;
	}

	private _setMouseProgress(frame: HomeWebGLFrame): void {
		this._sceneProps.mouse.progress.x =
			(-1 * frame.containerX + this._sceneProps.mouse.cords.x) / this._sceneProps.sizes.container.w;
		this._sceneProps.mouse.progress.y = this._sceneProps.mouse.cords.y / this._sceneProps.sizes.window.h;
	}
}
